import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Download,
  Youtube,
  Image as ImageIcon,
  Target,
  Wand2,
  Clock,
  Sparkles,
  Layers,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../../../firebase.init";
import { validateGeminiKey } from "../../../../../utils/geminiUtils";

const YoutubeThumbnailGenerator = () => {
  const navigate = useNavigate();
  // --- AUTH & CORE STATE ---
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingTools, setLoadingTools] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // --- FORM STATE ---
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [contentType, setContentType] = useState("Explainer");
  const [tone, setTone] = useState("Professional");
  const [thumbnailStyle, setThumbnailStyle] = useState("Professional");

  // --- OUTPUT STATE (IMAGE) ---
  const [generatedImage, setGeneratedImage] = useState(null);

  // --- CREDIT & TOOL CONFIG ---

  const [creditCost, setCreditCost] = useState(5);
  // --- OPTIONS ---
  const audienceOptions = [
    "General Audience",
    "Beginners",
    "Experts",
    "Professionals",
    "Gamers",
    "Students",
  ];
  const contentOptions = [
    "Tutorial",
    "Review",
    "Comparison",
    "List",
    "Explainer",
    "Reaction",
    "Documentary",
  ];
  const toneOptions = [
    "Bold",
    "Dramatic",
    "Energetic",
    "Curiosity-Based",
    "Professional",
    "Inspiring",
    "Mysterious",
  ];
  const styleOptions = [
    "Professional",
    "Minimal",
    "Face-Focused",
    "Text-Focused",
    "Object-Focused",
    "Split Comparison",
  ];

  // Fetch API keys

  // Fetch Tools List
  const fetchToolsList = async () => {
    try {
      const response = await axios.get(
        `https://server.enjoywiki.com/youtubeai-may/api/tools-section`,
      );
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.tools
      ) {
        const tool = response.data.data.tools.find((t) => t.id === 9);
        if (tool) {
          setCreditCost(tool.creditCost);
        }
      }
    } catch (err) {
      console.warn("Could not fetch tools list, using default credit cost");
    } finally {
      setLoadingTools(false);
    }
  };  // Initial Data Fetch
  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingTools(false);
        return;
      }
      try {
        await fetchToolsList();

        const userResponse = await axios.get(`https://server.enjoywiki.com/youtubeai-may/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          const userData = userResponse.data[0];
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  // Prompt Engineering for the Image Model
  const constructImagePrompt = () => {
    return `Create a viral, high-CTR (Click-Through Rate) YouTube thumbnail for a video titled: "${topic}".

    CRITICAL REQUIREMENTS:
    1. CLICKBAIT TEXT: Use a suitable short text on the thumbnail, high-impact clickbait text in the thumbnail. Select the most provocative 2-4 words related to the topic. The text should be massive, bold, 3D-style, and placed for maximum readability.
    2. VIRAL DESIGN: Use "Viral Style" aesthetics—ultra-vibrant colors, extreme contrast, and a cinematic masterpiece quality. Use lighting and composition that pops out of the YouTube homepage.
    3. VISUAL HOOK: Ensure a powerful focal point. If it's a person, use an exaggerated facial expression (shock, awe, intensity). If it's an object, make it look hyper-realistic and glowing.
    4. TRENDING ELEMENTS: Include subtle viral cues like a red arrow, a glowing circle, or a dramatic "Before vs After" split if appropriate for the topic.
    
    Target Audience: ${audience}.
    Content Type: ${contentType}.
    Vibe/Tone: ${tone}.
    Visual Style: ${thumbnailStyle}.
    
    Overall Aesthetic: Professional, high-production value, 8k resolution, cinematic lighting, and extremely eye-catching. No clutter, clear focus. Use a suitable short text on the thumbnail.`;
  };

  // Generate Image via Gemini
  const generateThumbnailImage = async () => {
    if (!topic.trim()) {
      setError("Please provide a Video Topic.");
      return;
    }
    setGenerating(true);
    setGeneratedImage(null);
    setError("");

    try {
      // Validate user Gemini key before generation
      const key = await validateGeminiKey(user.email);
      if (!key) { setGenerating(false); return; }

      // 1. Generate Prompt & Text with AI
      const MODEL_ID = localStorage.getItem("geminiImageModel") || "gemini-2.0-flash-exp-image-generation";
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${key}`;
      const prompt = constructImagePrompt();

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [{ text: prompt }],
            },
          ],
          generationConfig: {
            responseModalities: ["IMAGE"]
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `AI Studio Image Error: ${response.status} - ${errorText}`,
        );
      }

      const data = await response.json();


      let base64Image = null;
      let mimeType = "image/jpeg";
      let textFallback = "";

      // Parse response looking for inlineData
      if (
        data.candidates &&
        data.candidates[0].content &&
        data.candidates[0].content.parts
      ) {
        const parts = data.candidates[0].content.parts;

        // Find the image part
        const imagePart = parts.find((p) => p.inlineData);
        if (imagePart) {
          base64Image = imagePart.inlineData.data;
          mimeType = imagePart.inlineData.mimeType || "image/jpeg";
        }

        // Find text part
        const textPart = parts.find((p) => p.text);
        if (textPart) {
          textFallback = textPart.text;
        }
      }

      if (base64Image) {
        setGeneratedImage(`data:${mimeType};base64,${base64Image}`);
        toast.success("Thumbnail image generated successfully!");
      } else {
        console.error("API Response Data:", data);
        const errorMsg = textFallback
          ? `AI responded with text: "${textFallback}"`
          : "No image was returned from the AI response. See console for details.";
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error("Error generating image:", err);
      setError(`${err.message}`);
      toast.error(`Generation Failed. Please check errors.`);
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = `${topic.replace(/\s+/g, "-").toLowerCase()}-thumbnail.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Image downloaded successfully!");
  };

  if (loadingAuth || loading ||  loadingTools) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              border: "4px solid #f1f5f9",
              borderTop: "4px solid #8b5cf6",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              animation: "spin 1s linear infinite",
              margin: "0 auto 10px",
            }}
          ></div>
          <p style={{ color: "#64748b", fontWeight: "500" }}>
            Loading Workspace...
          </p>
        </div>
        <Toaster position="top-right" />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            background: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <ImageIcon
            size={48}
            color="#8b5cf6"
            style={{ marginBottom: "1rem" }}
          />
          <h2
            style={{
              color: "#0f172a",
              marginBottom: "1.5rem",
              fontSize: "1.5rem",
              fontWeight: "700",
            }}
          >
            Authentication Required
          </h2>
          <button
            onClick={() => (window.location.href = "/login")}
            style={{
              padding: "12px 24px",
              background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              width: "100%",
            }}
          >
            Proceed to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .thumb-app { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; min-height: 100vh; padding: 2rem 1rem; color: #334155; }
        .saas-card { background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; padding: 1.75rem; height: 100%; display: flex; flex-direction: column;}
        .form-label { font-weight: 600; color: #475569; font-size: 0.9rem; margin-bottom: 0.5rem; display: block; }
        .form-control, .form-select { width: 100%; padding: 0.85rem 1rem; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 0.95rem; color: #0f172a; transition: all 0.2s; margin-bottom: 1.25rem; box-sizing: border-box; }
        .form-control:focus, .form-select:focus { outline: none; border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1); background: #fff; }
        
        .gradient-btn { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); color: white; border: none; padding: 1rem 1.5rem; border-radius: 10px; font-weight: 600; font-size: 1rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem; transition: all 0.3s ease; width: 100%; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25); margin-top: auto;}
        .gradient-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35); }
        .gradient-btn:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
        
        .concept-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 2rem; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
        
        @keyframes custom-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(0.98); } }
        .anim-pulse { animation: custom-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>

      <div className="thumb-app">
        <Toaster position="top-right" />

        <div
          className="container"
          style={{ maxWidth: "1400px", margin: "0 auto" }}
        >
          <div className="row mb-4">
            <div
              className="col-12 d-flex justify-content-between align-items-center"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <button
                onClick={() => navigate('/')}
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "600",
                  color: "#64748b",
                }}
              >
                ← Dashboard
              </button>
            </div>
          </div>

          <div
            className="text-center mb-5"
            style={{ textAlign: "center", margin: "3rem 0" }}
          >
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "800",
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                letterSpacing: "-0.02em",
              }}
            >
              <ImageIcon color="#8b5cf6" size={40} /> AI Thumbnail Image
              Generator
            </h1>
            <p
              style={{
                color: "#64748b",
                marginTop: "12px",
                fontSize: "1.1rem",
                maxWidth: "650px",
                margin: "12px auto 0",
              }}
            >
              Turn your video topic into a high-CTR, fully rendered YouTube
              thumbnail image using AI.
            </p>
          </div>

          {error && (
            <div
              style={{
                background: "#fef2f2",
                color: "#ef4444",
                padding: "1rem",
                borderRadius: "10px",
                marginBottom: "2rem",
                textAlign: "center",
                border: "1px solid #fecaca",
                fontWeight: "600",
              }}
            >
              {error}
            </div>
          )}

          <div
            className="row"
            style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}
          >
            <div
              className="col-lg-4 col-md-5"
              style={{ flex: "1 1 350px", maxWidth: "450px" }}
            >
              <div className="saas-card">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "1.5rem",
                    paddingBottom: "1rem",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <Target size={20} color="#8b5cf6" />
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.25rem",
                      color: "#0f172a",
                      fontWeight: "700",
                    }}
                  >
                    Design Parameters
                  </h3>
                </div>

                <label className="form-label">
                  Video Topic <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., How to edit videos faster in Premiere Pro..."
                  rows={2}
                />

                <label className="form-label">Target Audience</label>
                <select
                  className="form-select"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                >
                  {audienceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Content Type</label>
                    <select
                      className="form-select"
                      value={contentType}
                      onChange={(e) => setContentType(e.target.value)}
                    >
                      {contentOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Vibe / Tone</label>
                    <select
                      className="form-select"
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    >
                      {toneOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="form-label">Primary Visual Style</label>
                <select
                  className="form-select"
                  value={thumbnailStyle}
                  onChange={(e) => setThumbnailStyle(e.target.value)}
                >
                  {styleOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>

                <button
                  onClick={generateThumbnailImage}
                  disabled={generating || !topic.trim()}
                  className="gradient-btn"
                >
                  {generating ? (
                    <Clock size={18} className="fa-spin" />
                  ) : (
                    <Sparkles size={18} />
                  )}
                  {generating ? "Rendering Image..." : `Generate Image `}
                </button>
              </div>
            </div>

            <div className="col-lg-8 col-md-7" style={{ flex: "1 1 500px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    color: "#0f172a",
                    fontWeight: "700",
                  }}
                >
                  Generated Thumbnail
                </h2>
              </div>

              {generating ? (
                <div
                  className="saas-card anim-pulse"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      background: "#f3e8ff",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 1.5rem",
                    }}
                  >
                    <Wand2 size={34} color="#8b5cf6" />
                  </div>
                  <h3
                    style={{
                      color: "#0f172a",
                      fontWeight: "800",
                      marginBottom: "0.5rem",
                    }}
                  >
                    AI is rendering your image...
                  </h3>
                  <p
                    style={{
                      color: "#64748b",
                      maxWidth: "350px",
                      margin: "0 auto",
                    }}
                  >
                    This usually takes about 10-20 seconds. Applying styles,
                    lighting, and composition.
                  </p>
                </div>
              ) : generatedImage ? (
                <div className="concept-card">
                  <div style={{ padding: "1.5rem" }}>
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        paddingTop: "56.25%",
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: "#e2e8f0",
                      }}
                    >
                      <img
                        src={generatedImage}
                        alt="Generated Thumbnail"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "1.5rem",
                      }}
                    >
                      <button
                        onClick={downloadImage}
                        className="gradient-btn"
                        style={{ maxWidth: "300px" }}
                      >
                        <Download size={18} /> Download Thumbnail
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="saas-card"
                  style={{
                    textAlign: "center",
                    padding: "6rem 2rem",
                    border: "2px dashed #cbd5e1",
                    background: "transparent",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ImageIcon
                    size={56}
                    color="#cbd5e1"
                    style={{ marginBottom: "1.5rem" }}
                  />
                  <h3
                    style={{
                      color: "#64748b",
                      fontWeight: "700",
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    Ready to Render
                  </h3>
                  <p style={{ color: "#94a3b8", margin: 0, maxWidth: "350px" }}>
                    Enter your video topic to generate a high-quality, perfectly
                    sized YouTube thumbnail image.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default YoutubeThumbnailGenerator;
